import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  ChevronDown,
  ChevronUp,
  Package,
  AlertCircle,
  Info,
  Plus,
} from 'lucide-react';
import Button from '@/components/ui/Button';
import type {
  AnalyzeResponse,
  MatchedFeature,
  GeneratedFeature,
  Addon,
  GeneratedAddon,
  MultiplierOverrides,
  RevenueModel,
  ProjectInputs,
  EstimateRequest,
} from '@/types/estimation';

// ─── Labels ────────────────────────────────────────────────────────

const REVENUE_LABELS: Record<RevenueModel, string> = {
  subscription: 'Abonnement',
  ads: 'Publicité',
  freemium: 'Freemium',
  commission: 'Commission',
  one_time: 'Achat unique',
  usage_based: "Facturation à l'usage",
};

const FEATURE_TYPE_LABELS: Record<string, { label: string; color: string }> = {
  core: { label: 'Essentiel', color: 'bg-green-100 text-green-700' },
  optional: { label: 'Optionnel', color: 'bg-blue-100 text-blue-700' },
  nfr: { label: 'NFR', color: 'bg-purple-100 text-purple-700' },
};

// ─── Explanation helper ─────────────────────────────────────────────

const ExplanationText: React.FC<{ text: string | null | undefined }> = ({ text }) => {
  if (!text) return null;
  return (
    <p className="text-xs text-slate-500 italic mt-1 leading-relaxed">
      <Info className="w-3 h-3 inline mr-1 -mt-0.5 text-slate-400" />
      {text}
    </p>
  );
};

// ─── Component ─────────────────────────────────────────────────────

interface ReviewSuggestionsProps {
  analysis: AnalyzeResponse;
  inputs: ProjectInputs;
  onSubmit: (request: EstimateRequest) => void;
  onBack: () => void;
}

const ReviewSuggestions: React.FC<ReviewSuggestionsProps> = ({
  analysis,
  inputs,
  onSubmit,
  onBack,
}) => {
  // Deep-clone so we don't mutate the original
  const [matchedFeatures, setMatchedFeatures] = useState<MatchedFeature[]>(
    analysis.matched_features.map((f) => ({
      ...f,
      sub_features: f.sub_features.map((sf) => ({ ...sf })),
      additional_sub_features: f.additional_sub_features?.map((asf) => ({ ...asf })),
    }))
  );

  const [generatedFeatures, setGeneratedFeatures] = useState<GeneratedFeature[]>(
    analysis.generated_features.map((f) => ({
      ...f,
      sub_features: f.sub_features.map((sf) => ({ ...sf })),
    }))
  );

  // Multiplier values (flat numbers) — initialised from the { value, explanation } objects
  const [multiplierValues, setMultiplierValues] = useState<MultiplierOverrides>({
    platform: analysis.suggested_multipliers.platform.value,
    scale: analysis.suggested_multipliers.scale.value,
    complexity: analysis.suggested_multipliers.complexity.value,
    maturity: analysis.suggested_multipliers.maturity.value,
  });

  const [addons, setAddons] = useState<Addon[]>(
    (analysis.suggested_addons || []).map((a) => ({ ...a }))
  );

  const [generatedAddons, setGeneratedAddons] = useState<GeneratedAddon[]>(
    (analysis.generated_addons || []).map((ga) => ({
      ...ga,
      sub_items: ga.sub_items?.map((si) => ({ ...si })),
    }))
  );

  const [revenueModels, setRevenueModels] = useState<RevenueModel[]>(
    analysis.suggested_revenue_models?.length
      ? analysis.suggested_revenue_models
      : inputs.revenueModels
  );

  // Start with all features expanded by default
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => {
    const ids = new Set<string>();
    analysis.matched_features.forEach((f) => {
      if (
        f.sub_features.length > 0 ||
        (f.additional_sub_features && f.additional_sub_features.length > 0)
      ) {
        ids.add(f.id);
      }
    });
    analysis.generated_features.forEach((f) => {
      if (f.sub_features.length > 0) {
        ids.add(f.temp_id);
      }
    });
    return ids;
  });

  // Track which generated addons are expanded to show sub_items
  const [expandedAddonIds, setExpandedAddonIds] = useState<Set<string>>(new Set());

  // ── Toggles ──

  const toggleMatchedFeature = (featureId: string) => {
    setMatchedFeatures((prev) =>
      prev.map((f) => (f.id === featureId ? { ...f, selected: !f.selected } : f))
    );
  };

  const toggleMatchedSubFeature = (featureId: string, subIndex: number) => {
    setMatchedFeatures((prev) =>
      prev.map((f) => {
        if (f.id !== featureId) return f;
        const subs = [...f.sub_features];
        subs[subIndex] = { ...subs[subIndex], selected: !subs[subIndex].selected };
        return { ...f, sub_features: subs };
      })
    );
  };

  const toggleAdditionalSubFeature = (featureId: string, subIndex: number) => {
    setMatchedFeatures((prev) =>
      prev.map((f) => {
        if (f.id !== featureId || !f.additional_sub_features) return f;
        const asfs = [...f.additional_sub_features];
        asfs[subIndex] = { ...asfs[subIndex], selected: !asfs[subIndex].selected };
        return { ...f, additional_sub_features: asfs };
      })
    );
  };

  const toggleGeneratedFeature = (tempId: string) => {
    setGeneratedFeatures((prev) =>
      prev.map((f) => (f.temp_id === tempId ? { ...f, selected: !f.selected } : f))
    );
  };

  const toggleGeneratedSubFeature = (tempId: string, subIndex: number) => {
    setGeneratedFeatures((prev) =>
      prev.map((f) => {
        if (f.temp_id !== tempId) return f;
        const subs = [...f.sub_features];
        subs[subIndex] = { ...subs[subIndex], selected: !subs[subIndex].selected };
        return { ...f, sub_features: subs };
      })
    );
  };

  const toggleAddon = (addonId: string) => {
    setAddons((prev) =>
      prev.map((a) => (a.id === addonId ? { ...a, selected: !a.selected } : a))
    );
  };

  const toggleGeneratedAddon = (tempId: string) => {
    setGeneratedAddons((prev) =>
      prev.map((ga) => (ga.temp_id === tempId ? { ...ga, selected: !ga.selected } : ga))
    );
  };

  const toggleRevenueModel = (model: RevenueModel) => {
    setRevenueModels((prev) =>
      prev.includes(model) ? prev.filter((m) => m !== model) : [...prev, model]
    );
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAddonExpand = (id: string) => {
    setExpandedAddonIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Submit ──

  const handleSubmit = () => {
    // Collect IDs of all sub-features the user kept selected across matched features
    const selectedSubFeatureIds: string[] = matchedFeatures
      .filter((f) => f.selected)
      .flatMap((f) =>
        f.sub_features
          .filter((sf) => sf.selected && sf.id)
          .map((sf) => sf.id as string)
      );

    const request: EstimateRequest = {
      mode: 'ai',
      template_id: analysis.template_id,
      platforms: inputs.platforms,
      revenue_models: revenueModels,
      project_description: inputs.projectDescription,
      selected_feature_ids: matchedFeatures.filter((f) => f.selected).map((f) => f.id),
      selected_sub_feature_ids: selectedSubFeatureIds,
      selected_addon_ids: addons.filter((a) => a.selected).map((a) => a.id),
      multiplier_overrides: multiplierValues,
      generated_features: generatedFeatures
        .filter((f) => f.selected)
        .map((f) => ({
          temp_id: f.temp_id,
          name: f.name,
          description: f.description,
          explanation: f.explanation,
          base_hours: f.base_hours,
          sub_features: f.sub_features
            .filter((sf) => sf.selected)
            .map((sf) => ({ name: sf.name, hours: sf.hours, explanation: sf.explanation })),
        })),
      feature_additions: matchedFeatures
        .filter((f) => f.additional_sub_features?.some((asf) => asf.selected))
        .map((f) => ({
          feature_id: f.id,
          additional_sub_features: (f.additional_sub_features || [])
            .filter((asf) => asf.selected)
            .map((asf) => ({ name: asf.name, hours: asf.hours, explanation: asf.explanation })),
        })),
      generated_addons: generatedAddons
        .filter((ga) => ga.selected)
        .map((ga) => ({
          temp_id: ga.temp_id,
          name: ga.name,
          description: ga.description,
          explanation: ga.explanation,
          hours: ga.hours,
          sub_items: ga.sub_items?.map((si) => ({
            name: si.name,
            hours: si.hours,
            explanation: si.explanation,
          })) ?? [],
        })),
      // Pass AI-generated explanations for matched features
      matched_feature_explanations: matchedFeatures
        .filter((f) => f.selected && f.explanation)
        .map((f) => ({
          feature_id: f.id,
          explanation: f.explanation as string,
        })),
    };
    onSubmit(request);
  };

  // ── Render helpers ──

  const FeatureCheckbox = ({
    checked,
    onClick,
    size = 'md',
  }: {
    checked: boolean;
    onClick: () => void;
    size?: 'sm' | 'md';
  }) => {
    const s = size === 'sm' ? 'w-5 h-5' : 'w-6 h-6';
    const icon = size === 'sm' ? 'w-3 h-3' : 'w-4 h-4';
    const c = checked
      ? 'border-orcsys-500 bg-orcsys-500'
      : 'border-slate-300 hover:border-orcsys-300';
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${s} rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${c}`}
        aria-label={checked ? 'Désélectionner' : 'Sélectionner'}
      >
        {checked && <CheckCircle className={`${icon} text-white`} />}
      </button>
    );
  };

  // Combine matched + generated features into one unified list
  type UnifiedFeature =
    | { kind: 'matched'; data: MatchedFeature }
    | { kind: 'generated'; data: GeneratedFeature };

  const allFeatures: UnifiedFeature[] = [
    ...matchedFeatures.map((f) => ({ kind: 'matched' as const, data: f })),
    ...generatedFeatures.map((f) => ({ kind: 'generated' as const, data: f })),
  ];

  const selectedCount = allFeatures.filter((f) => f.data.selected).length;

  // ── Main render ──

  return (
    <div className="w-full max-w-5xl mx-auto space-y-6">
      {/* Header card */}
      <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">
              Personnalisez votre estimation
            </h2>
            <p className="text-slate-600">
              Catégorie :{' '}
              <span className="font-medium text-slate-800">{analysis.project_category}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 px-4 py-3 bg-orcsys-50 rounded-xl">
            <CheckCircle className="w-5 h-5 text-orcsys-500" />
            <span className="text-lg font-bold text-orcsys-700">{selectedCount}</span>
            <span className="text-sm text-orcsys-600">fonctionnalités sélectionnées</span>
          </div>
        </div>

        <div className="mt-4 p-4 bg-blue-50 border border-blue-100 rounded-lg">
          <p className="text-sm text-blue-800 flex items-start gap-2">
            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              <strong>Cliquez sur une fonctionnalité</strong> pour l'ajouter ou la retirer de votre
              estimation. Chaque modification affectera le prix final.
            </span>
          </p>
        </div>

        {analysis.assumptions.length > 0 && (
          <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
            <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center">
              <AlertCircle className="w-4 h-4 mr-2" />
              Hypothèses
            </p>
            <ul className="space-y-1">
              {analysis.assumptions.map((a, i) => (
                <li key={i} className="text-sm text-slate-600">
                  • {a}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* ═══ Unified Feature List ═══ */}
      {allFeatures.length > 0 && (
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8">
          <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1 flex items-center">
            <CheckCircle className="w-5 h-5 mr-2 text-orcsys-500" />
            Fonctionnalités ({selectedCount}/{allFeatures.length})
          </h3>
          <p className="text-sm text-slate-500 mb-6">
            Sélectionnez ou désélectionnez les fonctionnalités pour ajuster le prix
          </p>

          <div className="space-y-3">
            {allFeatures.map((unified) => {
              if (unified.kind === 'matched') {
                const feature = unified.data;
                const expanded = expandedIds.has(feature.id);
                const badge = FEATURE_TYPE_LABELS[feature.type] || FEATURE_TYPE_LABELS.optional;
                const hasChildren =
                  feature.sub_features.length > 0 ||
                  (feature.additional_sub_features && feature.additional_sub_features.length > 0);

                return (
                  <div
                    key={feature.id}
                    className={`rounded-xl border-2 transition-all cursor-pointer ${
                      feature.selected
                        ? 'border-orcsys-500 bg-orcsys-50/30'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {/* Feature row */}
                    <div className="flex items-center p-4 gap-3">
                      <FeatureCheckbox
                        checked={feature.selected}
                        onClick={() => toggleMatchedFeature(feature.id)}
                      />
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => toggleMatchedFeature(feature.id)}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-slate-900">{feature.name}</h4>
                          <span
                            className={`px-2 py-0.5 text-xs rounded font-medium ${badge.color}`}
                          >
                            {badge.label}
                          </span>
                        </div>
                        <p className="text-sm text-slate-600 truncate">{feature.description}</p>
                        <ExplanationText text={feature.explanation} />
                      </div>
                      {hasChildren && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(feature.id);
                          }}
                          className="p-1 text-slate-400 hover:text-slate-600"
                          aria-label="Afficher les détails"
                        >
                          {expanded ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                      )}
                    </div>

                    {/* Sub-features + additional sub-features */}
                    {expanded && hasChildren && (
                      <div className="px-3 sm:px-4 pb-4 pt-2 border-t border-slate-100 ml-4 sm:ml-9 space-y-2">
                        {feature.sub_features.length > 0 && (
                          <>
                            <p className="text-xs text-slate-500 font-medium mb-1">
                              Sous-fonctionnalités — cliquez pour modifier
                            </p>
                            {feature.sub_features.map((sf, si) => (
                              <button
                                key={si}
                                type="button"
                                onClick={() => toggleMatchedSubFeature(feature.id, si)}
                                className={`flex flex-col w-full text-left px-2 py-1.5 rounded-lg transition-all ${
                                  sf.selected
                                    ? 'bg-orcsys-50 hover:bg-orcsys-100'
                                    : 'hover:bg-slate-50'
                                }`}
                              >
                                <div className="flex items-center gap-3">
                                  <FeatureCheckbox
                                    checked={sf.selected}
                                    onClick={() => toggleMatchedSubFeature(feature.id, si)}
                                    size="sm"
                                  />
                                  <span className="text-sm text-slate-700 flex-1">{sf.name}</span>
                                </div>
                                {sf.explanation && (
                                  <div className="ml-8">
                                    <ExplanationText text={sf.explanation} />
                                  </div>
                                )}
                              </button>
                            ))}
                          </>
                        )}

                        {/* Additional sub-features (opt-in, default OFF) */}
                        {feature.additional_sub_features &&
                          feature.additional_sub_features.length > 0 && (
                            <>
                              <div className="pt-2 mt-2 border-t border-dashed border-slate-200">
                                <p className="text-xs text-slate-500 font-medium mb-1 flex items-center gap-1">
                                  <Plus className="w-3 h-3" />
                                  Options avancées
                                </p>
                              </div>
                              {feature.additional_sub_features.map((asf, asi) => (
                                <button
                                  key={asi}
                                  type="button"
                                  onClick={() => toggleAdditionalSubFeature(feature.id, asi)}
                                  className={`flex flex-col w-full text-left px-2 py-1.5 rounded-lg transition-all ${
                                    asf.selected
                                      ? 'bg-orcsys-50 hover:bg-orcsys-100'
                                      : 'hover:bg-slate-50'
                                  }`}
                                >
                                  <div className="flex items-center gap-3">
                                    <FeatureCheckbox
                                      checked={asf.selected}
                                      onClick={() => toggleAdditionalSubFeature(feature.id, asi)}
                                      size="sm"
                                    />
                                    <span className="text-sm text-slate-700 flex-1">
                                      {asf.name}
                                    </span>
                                  </div>
                                  {asf.explanation && (
                                    <div className="ml-8">
                                      <ExplanationText text={asf.explanation} />
                                    </div>
                                  )}
                                </button>
                              ))}
                            </>
                          )}
                      </div>
                    )}
                  </div>
                );
              } else {
                // Generated feature
                const feature = unified.data;
                const expanded = expandedIds.has(feature.temp_id);

                return (
                  <div
                    key={feature.temp_id}
                    className={`rounded-xl border-2 transition-all cursor-pointer ${
                      feature.selected
                        ? 'border-orcsys-500 bg-orcsys-50/30'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center p-4 gap-3">
                      <FeatureCheckbox
                        checked={feature.selected}
                        onClick={() => toggleGeneratedFeature(feature.temp_id)}
                      />
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => toggleGeneratedFeature(feature.temp_id)}
                      >
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-semibold text-slate-900">{feature.name}</h4>
                        </div>
                        <p className="text-sm text-slate-600 truncate">{feature.description}</p>
                        <ExplanationText text={feature.explanation} />
                      </div>
                      {feature.sub_features.length > 0 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(feature.temp_id);
                          }}
                          className="p-1 text-slate-400 hover:text-slate-600"
                          aria-label="Afficher les détails"
                        >
                          {expanded ? (
                            <ChevronUp className="w-5 h-5" />
                          ) : (
                            <ChevronDown className="w-5 h-5" />
                          )}
                        </button>
                      )}
                    </div>

                    {expanded && feature.sub_features.length > 0 && (
                      <div className="px-3 sm:px-4 pb-4 pt-2 border-t border-slate-100 ml-4 sm:ml-9 space-y-2">
                        <p className="text-xs text-slate-500 font-medium mb-1">
                          Sous-fonctionnalités — cliquez pour modifier
                        </p>
                        {feature.sub_features.map((sf, si) => (
                          <button
                            key={si}
                            type="button"
                            onClick={() => toggleGeneratedSubFeature(feature.temp_id, si)}
                            className={`flex flex-col w-full text-left px-2 py-1.5 rounded-lg transition-all ${
                              sf.selected
                                ? 'bg-orcsys-50 hover:bg-orcsys-100'
                                : 'hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <FeatureCheckbox
                                checked={sf.selected}
                                onClick={() => toggleGeneratedSubFeature(feature.temp_id, si)}
                                size="sm"
                              />
                              <span className="text-sm text-slate-700 flex-1">{sf.name}</span>
                            </div>
                            {sf.explanation && (
                              <div className="ml-8">
                                <ExplanationText text={sf.explanation} />
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
            })}
          </div>
        </div>
      )}

      {/* Multipliers are still sent to the backend but hidden from the user */}

      {/* ═══ Add-ons (catalog + generated) ═══ */}
      {(addons.length > 0 || generatedAddons.length > 0) && (
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8">
          <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1 flex items-center">
            <Package className="w-5 h-5 mr-2 text-orcsys-500" />
            Modules complémentaires (
            {addons.filter((a) => a.selected).length +
              generatedAddons.filter((ga) => ga.selected).length}
            /{addons.length + generatedAddons.length})
          </h3>
          <p className="text-sm text-slate-500 mb-6">
            Services supplémentaires — cliquez pour ajouter ou retirer
          </p>

          <div className="space-y-3">
            {/* Catalog addons */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {addons.map((addon) => (
                <motion.button
                  key={addon.id}
                  type="button"
                  onClick={() => toggleAddon(addon.id)}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${
                    addon.selected
                      ? 'border-orcsys-500 bg-orcsys-50'
                      : 'border-slate-200 hover:border-orcsys-200'
                  }`}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-slate-900 text-sm">{addon.name}</h4>
                      <p className="text-xs text-slate-600 mt-1">{addon.description}</p>
                    </div>
                    <div
                      className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ml-3 ${
                        addon.selected ? 'border-orcsys-500 bg-orcsys-500' : 'border-slate-300'
                      }`}
                    >
                      {addon.selected && <CheckCircle className="w-3 h-3 text-white" />}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Generated addons — with explanation and sub_items */}
            {generatedAddons.length > 0 && (
              <>
                {addons.length > 0 && (
                  <p className="text-xs text-slate-500 font-medium pt-2">
                    Modules supplémentaires suggérés
                  </p>
                )}
                {generatedAddons.map((addon) => {
                  const isExpanded = expandedAddonIds.has(addon.temp_id);
                  const hasSubItems = addon.sub_items && addon.sub_items.length > 0;

                  return (
                    <div
                      key={addon.temp_id}
                      className={`rounded-xl border-2 transition-all ${
                        addon.selected
                          ? 'border-orcsys-500 bg-orcsys-50'
                          : 'border-slate-200 hover:border-orcsys-200'
                      }`}
                    >
                      <div
                        className="p-4 cursor-pointer"
                        onClick={() => toggleGeneratedAddon(addon.temp_id)}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-slate-900 text-sm">{addon.name}</h4>
                            </div>
                            <p className="text-xs text-slate-600 mt-1">{addon.description}</p>
                            <ExplanationText text={addon.explanation} />
                          </div>
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ml-3 mt-0.5 ${
                              addon.selected ? 'border-orcsys-500 bg-orcsys-500' : 'border-slate-300'
                            }`}
                          >
                            {addon.selected && <CheckCircle className="w-3 h-3 text-white" />}
                          </div>
                        </div>

                        {/* Sub-items toggle */}
                        {hasSubItems && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleAddonExpand(addon.temp_id);
                            }}
                            className="mt-2 text-xs text-orcsys-600 hover:text-orcsys-700 flex items-center gap-1"
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-3 h-3" />
                            ) : (
                              <ChevronDown className="w-3 h-3" />
                            )}
                            {isExpanded ? 'Masquer le détail' : 'Voir le détail'}
                          </button>
                        )}
                      </div>

                      {/* Expanded sub-items */}
                      {isExpanded && hasSubItems && (
                        <div className="px-4 pb-4 pt-1 border-t border-slate-100 space-y-1.5">
                          {addon.sub_items!.map((si, idx) => (
                            <div key={idx} className="flex flex-col py-1">
                              <div className="flex items-center text-sm">
                                <span className="text-slate-700">• {si.name}</span>
                              </div>
                              {si.explanation && (
                                <p className="text-xs text-slate-400 italic ml-3 mt-0.5">
                                  {si.explanation}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══ Always-Included Notice ═══ */}
      <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8">
        <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-100 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-green-800">Toujours inclus dans votre projet</p>
            <p className="text-sm text-green-700 mt-1">
              CI/CD, audit de sécurité et audit de performance sont systématiquement inclus dans
              chaque projet, sans surcoût.
            </p>
          </div>
        </div>
      </div>

      {/* ═══ Revenue Models ═══ */}
      <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8">
        <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1">Modèles de revenus</h3>
        <p className="text-sm text-slate-500 mb-4">Modifiez si nécessaire</p>
        <div className="flex flex-wrap gap-2">
          {(Object.entries(REVENUE_LABELS) as [RevenueModel, string][]).map(([value, label]) => {
            const isSelected = revenueModels.includes(value);
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleRevenueModel(value)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  isSelected
                    ? 'bg-orcsys-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {label}
                {isSelected && ' ✕'}
              </button>
            );
          })}
        </div>
      </div>

      {/* ═══ Navigation ═══ */}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-4">
        <Button variant="outline" onClick={onBack} className="flex items-center justify-center">
          <ChevronLeft className="w-4 h-4 mr-2" />
          Modifier les détails
        </Button>
        <Button
          onClick={handleSubmit}
          className="flex items-center justify-center bg-orcsys-500 hover:bg-orcsys-600"
        >
          Calculer l'estimation
          <ChevronRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default ReviewSuggestions;
